import {
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  FilePdfOutlined,
} from '@ant-design/icons'
import { Collapse, List, Popover, Spin } from 'antd'
import { PdfViewer } from 'features/files'
import { useRolodexSignedURLs } from 'features/rolodex'
import _ from 'lodash'
import { CSSProperties, FC, useMemo, useState } from 'react'
import CantileverTheme from 'styles/cantileverTheme'

type DocumentCategory =
  | 'DESIGN_DRAWING'
  | 'DATA_SHEET'
  | 'INSPECTION_REPORT'
  | 'UNCATEGORISED'
const PATH_TO_CATEGORY: Record<string, DocumentCategory> = {
  'nii_source/12-V-104 GA Drawing.pdf': 'DESIGN_DRAWING',
  'nii_source/12-V-104 Process Data Sheet.pdf': 'DATA_SHEET',
  'nii_source/General Inspection Report_2018.pdf': 'INSPECTION_REPORT',
  'nii_source/General Inspection Report_2021.pdf': 'INSPECTION_REPORT',
}
const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  DESIGN_DRAWING: 'Design Drawing',
  DATA_SHEET: 'Data Sheet',
  INSPECTION_REPORT: 'Inspection Report',
  UNCATEGORISED: 'Uncategorised',
}

export interface NiiSourcesFileListProps {
  fileRefs: ReadonlyArray<{ path: string; entry_id: string }>
  hoveredFilePath: string | null
}

const HOVER_COLOR = CantileverTheme.colors.warning['500']
const SELECTED_BG_COLOR = '#e6f7ff'
const SELECTED_TEXT_COLOR = '#1890ff'

function getListItemBgColor(selected: boolean, hovered: boolean): string {
  return selected ? SELECTED_BG_COLOR : hovered ? HOVER_COLOR : 'transparent'
}

function getListItemStyle(
  selected: boolean,
  hovered: boolean,
  clickable: boolean
): CSSProperties {
  return {
    ...(clickable ? { cursor: 'pointer' } : {}),
    backgroundColor: getListItemBgColor(selected, hovered), // Highlight selected item
    color: selected ? SELECTED_TEXT_COLOR : 'inherit', // Change text color if selected
  }
}

const FileListItem: FC<{
  fileName: string
  onClick?: () => void
  selected: boolean
  hovered: boolean
}> = ({ fileName, onClick, selected, hovered }) => {
  const itemStyle = useMemo(() => {
    return getListItemStyle(selected, hovered, !!onClick)
  }, [selected, hovered, onClick])
  return (
    <List.Item style={itemStyle} onClick={() => onClick?.()}>
      <FilePdfOutlined style={{ marginRight: 8, color: 'red', fontSize: 16 }} />
      {fileName}
    </List.Item>
  )
}

export const NiiSourcesFileList: FC<NiiSourcesFileListProps> = ({
  fileRefs,
  hoveredFilePath,
}) => {
  const categorisedPaths: Record<DocumentCategory, typeof fileRefs> = useMemo(
    () =>
      _.reduce(
        fileRefs,
        (acc, ref) => {
          const category = PATH_TO_CATEGORY[ref.path] ?? 'UNCATEGORISED'
          return {
            ...acc,
            [category]: [...acc[category], ref],
          }
        },
        {
          DESIGN_DRAWING: [] as typeof fileRefs,
          DATA_SHEET: [] as typeof fileRefs,
          INSPECTION_REPORT: [] as typeof fileRefs,
          UNCATEGORISED: [] as typeof fileRefs,
        }
      ),
    [fileRefs]
  )

  const { urls, isLoading: urlsLoading } = useRolodexSignedURLs(fileRefs)
  const [openFilePath, setOpenFilePath] = useState<string | null>(null)

  return (
    <Spin spinning={urlsLoading}>
      <Collapse
        collapsible='header'
        items={useMemo(
          () =>
            Object.entries(categorisedPaths).map(([category, refs]) => ({
              key: category,
              label: (
                <div
                  style={{
                    ...getListItemStyle(
                      refs.some((r) => r.path === openFilePath),
                      refs.some((r) => r.path === hoveredFilePath),
                      true
                    ),
                    width: '100%',
                    display: 'flex',
                    gap: '0.5rem',
                  }}>
                  {refs.length ? (
                    <CheckCircleTwoTone
                      twoToneColor={CantileverTheme.colors.LimeGreen}
                    />
                  ) : category === 'UNCATEGORISED' ? (
                    <></>
                  ) : (
                    <ExclamationCircleTwoTone
                      twoToneColor={CantileverTheme.colors.DeepRed}
                    />
                  )}
                  {DOCUMENT_CATEGORY_LABELS[category as DocumentCategory]}
                </div>
              ),
              children: (
                <List
                  dataSource={[...refs]}
                  renderItem={(ref, idx) => {
                    const presignedUrl =
                      urls.length > idx ? urls[idx] : undefined
                    return (
                      <Popover
                        placement='right'
                        title={ref.path}
                        open={ref.path === openFilePath}
                        content={
                          presignedUrl ? (
                            <div
                              style={{
                                width: '40vw',
                                maxWidth: '100%',
                                margin: '0 auto',
                              }}>
                              <PdfViewer url={presignedUrl} />
                            </div>
                          ) : undefined
                        }>
                        <FileListItem
                          fileName={ref.path.substring('nii_source/'.length)}
                          onClick={() =>
                            setOpenFilePath(
                              ref.path === openFilePath ? null : ref.path
                            )
                          }
                          selected={openFilePath === ref.path}
                          hovered={hoveredFilePath === ref.path}
                        />
                      </Popover>
                    )
                  }}
                />
              ),
            })),
          [categorisedPaths, hoveredFilePath, openFilePath, urls]
        )}
      />
    </Spin>
  )
}
